import { useEffect, useMemo, useState } from 'react';
import {
  FileText, Save, History, RotateCcw, Settings, Sparkles,
  ChevronDown, ChevronRight, AlertCircle, Check,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Mapping des clés techniques de prompt → label humain affiché dans la sidebar.
   Aligné avec PROMPT_SECTION_KEYS du backend (models_cabinet_report). */
const PROMPT_LABELS = {
  '1::system_master':      { label: 'Identité éditoriale', sub: 'Niveau 1 · maître' },
  '2::letter':             { label: 'Lettre du cabinet', sub: 'Niveau 2' },
  '2::context':            { label: 'I — Contexte officinal', sub: 'Niveau 2' },
  '2::global_position':    { label: 'II — Position globale', sub: 'Niveau 2' },
  '2::alerts':             { label: 'III — Signaux clés', sub: 'Niveau 2' },
  '2::family_activite':    { label: 'IV — Activité commerciale', sub: 'Niveau 2' },
  '2::family_rentabilite': { label: 'IV — Rentabilité', sub: 'Niveau 2' },
  '2::family_stock':       { label: 'IV — Stock & trésorerie', sub: 'Niveau 2' },
  '2::family_patientele':  { label: 'IV — Patientèle & services', sub: 'Niveau 2' },
  '2::action_plan':        { label: "V — Plan d'action", sub: 'Niveau 2' },
};

const PROMPT_ORDER = Object.keys(PROMPT_LABELS);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

function authHeaders() {
  const token = localStorage.getItem('benchory_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function PromptList({ prompts, selectedKey, onSelect }) {
  return (
    <ul className="space-y-1" data-testid="prompt-list">
      {PROMPT_ORDER.map((key) => {
        const meta = PROMPT_LABELS[key];
        const active = prompts[key];
        const isSelected = selectedKey === key;
        return (
          <li key={key}>
            <button
              type="button"
              onClick={() => onSelect(key)}
              data-testid={`prompt-list-item-${key}`}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-all border ${
                isSelected
                  ? 'bg-[#1B2A4A] text-white border-[#1B2A4A] shadow-sm'
                  : 'bg-white text-[#1B2A4A] border-gray-100 hover:border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold leading-tight">{meta.label}</div>
                {active && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    V{active.version}
                  </span>
                )}
              </div>
              <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${
                isSelected ? 'text-white/70' : 'text-gray-400'
              }`}>
                {meta.sub}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function PromptEditor({ promptKey, current, onSaved }) {
  const [content, setContent] = useState(current?.content || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => { setContent(current?.content || ''); setError(''); }, [current?.id]);

  const dirty = content !== (current?.content || '');
  const meta = PROMPT_LABELS[promptKey];
  const [level, sectionKey] = promptKey.split('::');

  const handleSave = async () => {
    if (!content.trim()) { setError('Contenu vide refusé.'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}/admin/report-prompts`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ level: parseInt(level, 10), section_key: sectionKey, content }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Échec de la sauvegarde');
      }
      const saved = await res.json();
      onSaved?.(saved);
      // Refresh history if it was open
      if (historyOpen) loadHistory();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/admin/report-prompts/history?level=${level}&section_key=${encodeURIComponent(sectionKey)}`,
        { headers: authHeaders() },
      );
      if (res.ok) setHistory(await res.json());
    } catch { /* silent */ }
    setHistoryLoading(false);
  };

  const handleToggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history.length === 0) loadHistory();
  };

  const handleRestore = async (id) => {
    if (!window.confirm('Restaurer cette version comme version active ?')) return;
    try {
      const res = await fetch(`${API_URL}/admin/report-prompts/${id}/activate`, {
        method: 'POST', headers: authHeaders(),
      });
      if (res.ok) {
        const restored = await res.json();
        onSaved?.(restored);
        loadHistory();
      }
    } catch (e) { setError(e.message); }
  };

  if (!current) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
        <FileText size={32} className="text-gray-300 mx-auto mb-4" />
        <p className="text-sm text-gray-500">Sélectionnez un prompt à gauche pour l'éditer.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid={`prompt-editor-${promptKey}`}>
      <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-bold text-[#1B2A4A] font-manrope">{meta.label}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Version active V{current.version} · créée le {fmtDate(current.created_at)} par {current.created_by || '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="inline-flex items-center gap-1.5 text-[10px] text-amber-600 uppercase tracking-wider font-bold">
              <AlertCircle size={11} /> Modifications non sauvegardées
            </span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            data-testid="prompt-save-btn"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition ${
              dirty && !saving
                ? 'bg-[#1B2A4A] text-white hover:bg-[#152039]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Save size={13} /> {saving ? 'Sauvegarde…' : `Enregistrer comme V${current.version + 1}`}
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
          <AlertCircle size={13} /> {error}
        </div>
      )}

      <div className="p-6">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          data-testid="prompt-textarea"
          className="w-full font-mono text-[12px] text-[#1B2A4A] bg-[#F8F9FA] border border-gray-200 rounded-lg p-4 leading-relaxed focus:outline-none focus:border-[#1B2A4A] focus:ring-2 focus:ring-[#1B2A4A]/10 resize-y"
          rows={28}
          spellCheck={false}
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-gray-400">{content.length.toLocaleString('fr-FR')} caractères</span>
        </div>
      </div>

      <div className="border-t border-gray-100">
        <button
          type="button"
          onClick={handleToggleHistory}
          data-testid="prompt-history-toggle"
          className="w-full px-6 py-3 flex items-center justify-between text-sm text-gray-600 hover:bg-gray-50 transition"
        >
          <span className="inline-flex items-center gap-2 font-medium">
            <History size={14} /> Historique des versions
            {history.length > 0 && (
              <span className="text-[10px] text-gray-400">({history.length})</span>
            )}
          </span>
          {historyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {historyOpen && (
          <div className="px-6 pb-6 space-y-2" data-testid="prompt-history">
            {historyLoading ? (
              <div className="py-4 text-center text-xs text-gray-400">Chargement…</div>
            ) : history.length === 0 ? (
              <div className="py-4 text-center text-xs text-gray-400">Aucune version archivée.</div>
            ) : (
              history.map((v) => (
                <div
                  key={v.id}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${
                    v.is_active ? 'border-[#1B2A4A] bg-[#1B2A4A]/[0.04]' : 'border-gray-100 bg-white'
                  }`}
                  data-testid={`prompt-history-row-${v.version}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-bold text-[#1B2A4A]">V{v.version}</span>
                    <span className="text-[11px] text-gray-500">
                      {fmtDate(v.created_at)} · {v.created_by || '—'}
                    </span>
                    {v.is_active && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-600 inline-flex items-center gap-1">
                        <Check size={10} /> Active
                      </span>
                    )}
                  </div>
                  {!v.is_active && (
                    <button
                      type="button"
                      onClick={() => handleRestore(v.id)}
                      data-testid={`prompt-restore-btn-${v.version}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-semibold text-[#B8860B] border border-[#B8860B]/30 hover:bg-[#B8860B]/5 transition"
                    >
                      <RotateCcw size={11} /> Restaurer
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ScoringSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/admin/report-settings`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setSettings)
      .catch(() => setError('Chargement des réglages échoué.'));
  }, []);

  if (!settings) {
    return <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-sm text-gray-400">Chargement…</div>;
  }

  const weights = settings.scoring_weights || {};
  const total = Object.values(weights).reduce((a, b) => a + (Number(b) || 0), 0);

  const updateWeight = (k, v) => {
    setSettings({ ...settings, scoring_weights: { ...weights, [k]: parseFloat(v) || 0 } });
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSavedAt(null);
    try {
      const res = await fetch(`${API_URL}/admin/report-settings`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scoring_weights: settings.scoring_weights,
          min_signal_score: settings.min_signal_score,
          max_signals_per_report: settings.max_signals_per_report,
          redundancy_lookback_months: settings.redundancy_lookback_months,
        }),
      });
      if (!res.ok) throw new Error('Échec de la sauvegarde');
      const saved = await res.json();
      setSettings(saved);
      setSavedAt(new Date());
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const FACTOR_LABELS = {
    magnitude: 'Magnitude (force du signal)',
    pertinence: 'Pertinence (lien officine ce mois)',
    freshness: 'Fraîcheur (récence du signal)',
    redundancy: 'Non-redondance (vs derniers rapports)',
  };

  return (
    <div className="space-y-6" data-testid="scoring-settings">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h2 className="font-bold text-[#1B2A4A] font-manrope mb-1 flex items-center gap-2">
          <Sparkles size={16} className="text-[#B8860B]" /> Pondération du scoring satellite
        </h2>
        <p className="text-xs text-gray-400 mb-5">
          Les 4 facteurs déterminent quels signaux contextuels sont injectés dans le rapport mensuel. Les valeurs sont re-normalisées automatiquement à l'usage si la somme s'éloigne de 1.0.
        </p>

        <div className="space-y-4">
          {Object.keys(FACTOR_LABELS).map((k) => (
            <div key={k} className="flex items-center gap-4" data-testid={`weight-${k}`}>
              <label className="flex-1 text-sm text-gray-700">{FACTOR_LABELS[k]}</label>
              <input
                type="number" step="0.05" min="0" max="1"
                value={weights[k] ?? 0}
                onChange={(e) => updateWeight(k, e.target.value)}
                className="w-24 h-9 px-3 rounded-lg border border-gray-200 text-sm text-right focus:outline-none focus:border-[#1B2A4A]"
              />
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Somme : <strong className={Math.abs(total - 1) < 0.01 ? 'text-emerald-600' : 'text-amber-600'}>{total.toFixed(2)}</strong>
            {Math.abs(total - 1) >= 0.01 && (
              <span className="ml-2 text-amber-600">(re-normalisée à l'usage)</span>
            )}
          </span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h2 className="font-bold text-[#1B2A4A] font-manrope mb-1 flex items-center gap-2">
          <Settings size={16} className="text-gray-500" /> Seuils et limites
        </h2>
        <div className="space-y-4 mt-4">
          <div className="flex items-center gap-4">
            <label className="flex-1 text-sm text-gray-700">
              Score minimum pour inclusion d'un signal
              <span className="block text-[10px] text-gray-400 mt-0.5">Entre 0 et 1, défaut 0.30</span>
            </label>
            <input
              type="number" step="0.05" min="0" max="1"
              value={settings.min_signal_score ?? 0}
              onChange={(e) => setSettings({ ...settings, min_signal_score: parseFloat(e.target.value) || 0 })}
              className="w-24 h-9 px-3 rounded-lg border border-gray-200 text-sm text-right focus:outline-none focus:border-[#1B2A4A]"
              data-testid="setting-min-score"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex-1 text-sm text-gray-700">
              Nombre maximum de signaux par rapport
              <span className="block text-[10px] text-gray-400 mt-0.5">Entre 0 et 20, défaut 5</span>
            </label>
            <input
              type="number" step="1" min="0" max="20"
              value={settings.max_signals_per_report ?? 0}
              onChange={(e) => setSettings({ ...settings, max_signals_per_report: parseInt(e.target.value) || 0 })}
              className="w-24 h-9 px-3 rounded-lg border border-gray-200 text-sm text-right focus:outline-none focus:border-[#1B2A4A]"
              data-testid="setting-max-signals"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex-1 text-sm text-gray-700">
              Profondeur d'historique pour la non-redondance (mois)
              <span className="block text-[10px] text-gray-400 mt-0.5">Entre 0 et 12, défaut 2</span>
            </label>
            <input
              type="number" step="1" min="0" max="12"
              value={settings.redundancy_lookback_months ?? 0}
              onChange={(e) => setSettings({ ...settings, redundancy_lookback_months: parseInt(e.target.value) || 0 })}
              className="w-24 h-9 px-3 rounded-lg border border-gray-200 text-sm text-right focus:outline-none focus:border-[#1B2A4A]"
              data-testid="setting-redundancy"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="flex items-center justify-between">
        {savedAt && (
          <span className="text-xs text-emerald-600 inline-flex items-center gap-1.5">
            <Check size={13} /> Réglages enregistrés à {savedAt.toLocaleTimeString('fr-FR')}
          </span>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="settings-save-btn"
          className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#1B2A4A] text-white hover:bg-[#152039] disabled:opacity-50 transition"
        >
          <Save size={14} /> {saving ? 'Sauvegarde…' : 'Enregistrer les réglages'}
        </button>
      </div>
    </div>
  );
}

export default function ReportPromptsAdminPage() {
  const [tab, setTab] = useState('prompts');  // 'prompts' | 'settings'
  const [activePrompts, setActivePrompts] = useState({});
  const [selectedKey, setSelectedKey] = useState('1::system_master');
  const [loading, setLoading] = useState(true);

  const loadPrompts = () => {
    setLoading(true);
    fetch(`${API_URL}/admin/report-prompts/active`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((arr) => {
        const dict = {};
        (arr || []).forEach((p) => {
          dict[`${p.level}::${p.section_key}`] = p;
        });
        setActivePrompts(dict);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadPrompts(); }, []);

  const handleSaved = (saved) => {
    const key = `${saved.level}::${saved.section_key}`;
    setActivePrompts((prev) => ({ ...prev, [key]: saved }));
  };

  return (
    <div className="space-y-6" data-testid="report-prompts-admin-page">
      <div>
        <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Prompts du Cabinet Benchory</h1>
        <p className="text-gray-400 mt-1 text-sm">
          Édition versionnée des prompts du rapport mensuel cabinet et des réglages du scoring satellite.
        </p>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { k: 'prompts', l: 'Prompts versionnés', icon: FileText },
          { k: 'settings', l: 'Réglages scoring', icon: Settings },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-2 ${
                tab === t.k ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500'
              }`}
            >
              <Icon size={14} /> {t.l}
            </button>
          );
        })}
      </div>

      {tab === 'prompts' && (
        loading ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-sm text-gray-400">
            Chargement des prompts…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
            <div className="bg-[#F8F9FA] rounded-xl border border-gray-100 p-3">
              <PromptList prompts={activePrompts} selectedKey={selectedKey} onSelect={setSelectedKey} />
            </div>
            <PromptEditor
              promptKey={selectedKey}
              current={activePrompts[selectedKey]}
              onSaved={handleSaved}
            />
          </div>
        )
      )}

      {tab === 'settings' && <ScoringSettingsPanel />}
    </div>
  );
}
